import {
  Apple,
  Scale,
  Smile,
  Dumbbell,
  Pill,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * SPEC 3.1.2 #10 — 5-button quick-log strip.
 * Inline at the bottom of Today (Dashboard mode). Tap → bottom sheet on mobile,
 * right-side drawer on desktop.
 *
 * BW7 mobile pass: icons enlarged to 28px (visual) with a 44px tap target so
 * touch targets meet HIG. Labels at 13px fall back to 12px on the narrowest
 * viewports to keep five buttons fitting comfortably.
 */

export type QuickLogAction =
  | "log_meal"
  | "log_weight"
  | "log_mood"
  | "log_workout"
  | "log_supplement";

const BUTTONS: { action: QuickLogAction; label: string; icon: LucideIcon }[] = [
  { action: "log_meal", label: "Meal", icon: Apple },
  { action: "log_weight", label: "Weight", icon: Scale },
  { action: "log_mood", label: "Mood", icon: Smile },
  { action: "log_workout", label: "Workout", icon: Dumbbell },
  { action: "log_supplement", label: "Supplement", icon: Pill },
];

export function QuickLogStrip({
  onPick,
}: {
  onPick: (action: QuickLogAction) => void;
}) {
  return (
    <section
      aria-label="Quick log"
      className="haus-quicklog-strip"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${BUTTONS.length}, 1fr)`,
        gap: "var(--space-2)",
        padding: "var(--space-3)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <style>{`
        .haus-quicklog-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 12px 8px;
          background: transparent;
          color: var(--fg-2);
          border: 1px solid transparent;
          border-radius: var(--radius-card);
          font-size: 13px;
          font-family: var(--font-sans);
          transition: background var(--dur-fast) var(--ease-out),
            color var(--dur-fast) var(--ease-out);
          min-height: 64px;
        }
        .haus-quicklog-btn:hover {
          background: var(--chip);
          color: var(--fg);
        }
        .haus-quicklog-btn:focus-visible {
          color: var(--fg);
        }
        @media (max-width: 380px) {
          .haus-quicklog-btn { font-size: 12px; padding: 10px 4px; }
        }
      `}</style>
      {BUTTONS.map((b) => (
        <button
          key={b.action}
          type="button"
          onClick={() => onPick(b.action)}
          className="haus-quicklog-btn"
        >
          <b.icon size={28} strokeWidth={1.5} aria-hidden />
          <span>{b.label}</span>
        </button>
      ))}
    </section>
  );
}
